import type { ReactNode } from 'react';

/**
 * Status badge — design-language §6. One tint recipe (theme.css `.badge`), semantic
 * tones here, and ONE central status→tone Record per entity (added in V1 as each
 * entity's real status vocabulary gets wired — never invent statuses the server
 * doesn't have). Law: exactly one status vocabulary per entity, plain English.
 */
export type Tone = 'info' | 'success' | 'warn' | 'danger' | 'brand' | 'neutral';

export function StatusBadge({ tone, children }: { tone: Tone; children: ReactNode }) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

/**
 * A block-level caveat in the same tone vocabulary as StatusBadge — for when a figure
 * on screen is knowably incomplete and the qualification has to sit with the number
 * rather than in a footnote nobody reads.
 *
 * `alert` makes it `role="alert"` (an assertive live region): a screen reader announces it
 * as it appears, wherever focus is. For a refusal that answers a press, such as a conversion
 * the server turned down, which a sighted person sees appear and a screen-reader user would
 * otherwise never hear. Opt-in, because a notice that is simply part of a page must not
 * interrupt.
 */
export function Notice({
  tone,
  alert = false,
  children,
}: {
  tone: Tone;
  alert?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`notice badge--${tone}`} role={alert ? 'alert' : undefined}>
      {children}
    </div>
  );
}

/** Dot + text for date/SLA states (running late, on time) — §6.1. */
export function StatusDot({ tone, children }: { tone: Tone; children?: ReactNode }) {
  return (
    <span
      className={`badge--${tone} inline-flex items-center gap-1.5 bg-transparent p-0`}
    >
      <span className="status-dot" />
      {children ? <span className="text-xs font-medium">{children}</span> : null}
    </span>
  );
}
